'use client';

import * as React from 'react';

import { CancelRounded, PlayArrow } from '@mui/icons-material';
import {
  DialogTitle,
  IconButton,
  DialogContent,
  Typography,
  DialogActions,
  Dialog,
  Button,
  Avatar as MUIAvatar,
  Stack,
  Grid,
  Box,
} from '@mui/material';
import { LearningPathByIdEventKeys, LearningPathByIdEvents } from '../../../events';
import { EventBus } from '@/shared/utils';
import { ModalType } from '../../../types/modals';
import { Auth } from 'firebase/auth';
import { MuxVideoPlayerWithToken } from '../mux-video-player-with-token';
import { useLearningPathByIdStore } from '../../../store/learning-path-by-id-store';

interface VideoLessonDialogProps {
  eventBus: EventBus<LearningPathByIdEvents>;
  auth: Auth | null;
  learningPathDocumentId: string;
}

type AdaptedModalType = ModalType & { playbackIdToken?: string | null };

const VideoLessonDialog: React.FC<VideoLessonDialogProps> = ({
  eventBus,
  auth,
  learningPathDocumentId,
}) => {
  const { summaryDetails } = useLearningPathByIdStore();
  const [openDialog, setOpenDialog] = React.useState<AdaptedModalType | null>(null);

  React.useEffect(() => {
    const handleVideoLessonDialog = (data: ModalType | null) => setOpenDialog(data);

    eventBus.on(LearningPathByIdEventKeys.modals.videoModal, handleVideoLessonDialog);
    return () => {
      eventBus.off(LearningPathByIdEventKeys.modals.videoModal, handleVideoLessonDialog);
    };
  }, [eventBus]);

  return (
    <Dialog open={!!openDialog} onClose={() => setOpenDialog(null)} maxWidth="md" fullWidth>
      <DialogTitle className="flex items-center justify-between p-6">
        {openDialog?.title}
        <IconButton aria-label="close" onClick={() => setOpenDialog(null)}>
          <CancelRounded className="text-action-divider" />
        </IconButton>
      </DialogTitle>

      <DialogContent className="pt-0 gap-y-6">
        {!!openDialog?.playbackId && (
          <div className="pb-4">
            <MuxVideoPlayerWithToken
              auth={auth}
              playbackId={openDialog?.playbackId}
              duration={openDialog?.duration || 0}
              teacherProfileDocumentId={
                summaryDetails?.data?.teacherProfiles?.[0]?.documentId || ''
              }
              title={openDialog?.title || ''}
              lessonDocumentId={openDialog?.lessonDocumentId || ''}
              lessonProgress={openDialog?.lessonProgress || null}
              eventBus={eventBus}
              learningPathDocumentId={learningPathDocumentId}
            />
          </div>
        )}

        <Grid container columnSpacing={3} rowSpacing={{ xs: 2, md: 0 }}>
          <Grid item xs={12} md={8}>
            <Typography variant="body-500">{openDialog?.description}</Typography>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="s2" color="textSecondary" component="p" className="mb-2">
              Insegnante
            </Typography>
            {!!openDialog?.instructors && openDialog?.instructors?.length > 0 ? (
              <Stack spacing={1}>
                {openDialog?.instructors.map((instructor, index) => (
                  <div key={index} className="flex items-center gap-x-2">
                    <MUIAvatar
                      src={instructor.avatarUrl}
                      alt={`${instructor.name}'s avatar`}
                      sx={{ height: 32, width: 32 }}
                    />
                    <div className="flex flex-col">
                      <Typography variant="caption-500" className="leading-none">
                        {instructor.name}
                      </Typography>
                      <Typography variant="caption-500" color="textSecondary">
                        {instructor.profession}
                      </Typography>
                    </div>
                  </div>
                ))}
              </Stack>
            ) : (
              <Typography>Nessun insegnante disponibile</Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => eventBus.emit(LearningPathByIdEventKeys.redirects.to, '/abbonati')}
          size="medium"
          color="primary"
          variant="contained"
          className="gap-2"
        >
          <PlayArrow />
          <Typography variant="button-m">Accedi a tutti i corsi</Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export { VideoLessonDialog };
